import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';

import { Button } from 'components/Buttons';

import {
  ImageWrapper,
  MosaicCell,
  MosaicColumn,
  MosaicWrapper,
} from './TextWithImage.styles';

import { colors } from 'styles';
import { IApplicationImageMosaic, IApplicationTextImageData } from 'types/applications';
import { useIsMobile } from 'hooks/useIsMobile';

type TextWithImageProps = IApplicationTextImageData & {
  type?: 'default' | 'customer-case';
  reversed?: boolean;
};

const ImageMosaic: FC<{ images: IApplicationImageMosaic }> = ({ images }) => (
  <MosaicWrapper>
    <MosaicColumn>
      <MosaicCell variant="small">
        <img src={images.firstImage.imageSrc} alt={images.firstImage.altText} />
      </MosaicCell>
      <MosaicCell variant="big">
        <img src={images.secondImage.imageSrc} alt={images.secondImage.altText} />
      </MosaicCell>
    </MosaicColumn>
    <MosaicColumn>
      <MosaicCell variant="big">
        <img src={images.thirdImage.imageSrc} alt={images.thirdImage.altText} />
      </MosaicCell>
      <MosaicCell variant="small">
        <img src={images.fourthImage.imageSrc} alt={images.fourthImage.altText} />
      </MosaicCell>
    </MosaicColumn>
  </MosaicWrapper>
);

const TextWithImage: FC<TextWithImageProps> = (props) => {
  const { type = 'default', reversed = false, description, title, link, listItems } = props;
  const navigate = useNavigate();
  const isDefault = type === 'default';
  const isMobile = useIsMobile();

  return (
    <Box
      display="flex"
      justifyContent="center"
      padding={{
        lg: '0px 0px',
        sm: '0px 24px',
        xxs: '0px 16px',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        maxWidth={1200}
        width="100%"
        padding={{
          '834px': '0px',
        }}
        gap={{
          md: '80px',
          sm: '36px',
          xxs: '36px',
        }}
        flexDirection={{
          md: 'row',
          sm: 'column',
          xxs: 'column',
        }}
        justifyContent={{
          md: 'space-between',
          sm: 'center',
          xxs: 'inherit',
        }}
      >
        <ImageWrapper
          sx={{
            order: {
              lg: reversed ? 1 : 0,
              md: reversed ? 1 : 0,
              sm: 0,
              xxs: 0,
            },
          }}
        >
          {props.images ? (
            <ImageMosaic images={props.images} />
          ) : (
            <img src={props.imageSrc} alt="application" />
          )}
        </ImageWrapper>
        <Box
          maxWidth={{
            lg: '560px',
            md: '420px',
            sm: '100%',
            xxs: '100%',
          }}
          sx={{
            order: {
              lg: reversed ? 0 : 1,
              md: reversed ? 0 : 1,
              sm: 1,
              xxs: 1,
            },
          }}
        >
          <Typography
            component="h3"
            fontSize={{
              md: '40px',
              xxs: '32px',
            }}
            color={colors.mainDark}
            fontWeight={700}
            lineHeight="120%"
            marginBottom="24px"
            maxWidth={{
              lg: '560px',
              md: '420px',
              sm: '100%',
              xxs: '100%',
            }}
          >
            {title}
          </Typography>

          <Typography color={colors.mainDark} fontSize="18px">
            {description}
          </Typography>

          {isDefault && listItems && (
            <>
              <Typography
                marginTop="32px"
                marginBottom="16px"
                color={colors.mainDark}
                fontWeight={700}
                lineHeight="140%"
                fontSize="20px"
              >
                Key Challenges:
              </Typography>

              <ul style={{ margin: 0, padding: 0, paddingLeft: '20px' }}>
                {listItems.map((i) => (
                  <li
                    key={i}
                    style={{
                      color: colors.mainDark,
                      lineHeight: '150%',
                    }}
                  >
                    {i}
                  </li>
                ))}
              </ul>
            </>
          )}

          {!isDefault && (
            <Box marginTop="24px">
              <Button
                width={isMobile ? 'full' : 234}
                onClick={() => {
                  if (link) {
                    navigate(link);
                  }
                }}
              >
                View Customer Case
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TextWithImage;
